import type { AreaDto, Company } from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';

/**
 * The global company/area filter from the top bar. It is deliberately app-wide:
 * dashboard, editorial plan and KPI all narrow to the same selection (06).
 */
interface FilterContextValue {
  company: Company | 'ALLE';
  areaId: string | null;
  /** Hides everything the seed script fabricated, on every surface at once. */
  hideDemo: boolean;
  setCompany: (company: Company | 'ALLE') => void;
  setAreaId: (areaId: string | null) => void;
  setHideDemo: (hide: boolean) => void;
  areas: AreaDto[];
  /** Areas matching the current company selection. */
  visibleAreas: AreaDto[];
  areaById: (id: string | null | undefined) => AreaDto | undefined;
  isLoading: boolean;
}

const FilterContext = createContext<FilterContextValue | null>(null);
const STORAGE_KEY = 'nova.filter';

export function FilterProvider({ children }: { children: React.ReactNode }) {
  const [company, setCompany] = useState<Company | 'ALLE'>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored).company ?? 'ALLE') : 'ALLE';
  });
  const [areaId, setAreaId] = useState<string | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored).areaId ?? null) : null;
  });
  const [hideDemo, setHideDemo] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? (JSON.parse(stored).hideDemo ?? false) : false;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ company, areaId, hideDemo }));
  }, [company, areaId, hideDemo]);

  const { data: areas = [], isLoading } = useQuery({
    queryKey: ['areas'],
    queryFn: () => api.get<AreaDto[]>('/areas'),
    staleTime: 5 * 60_000,
  });

  const visibleAreas = useMemo(
    () =>
      company === 'ALLE'
        ? areas
        : areas.filter((a) => a.company === company || a.company === 'BEGGE'),
    [areas, company],
  );

  // Drop an area selection that no longer belongs to the chosen company.
  useEffect(() => {
    if (areaId && !visibleAreas.some((a) => a.id === areaId)) setAreaId(null);
  }, [areaId, visibleAreas]);

  const value = useMemo<FilterContextValue>(
    () => ({
      company,
      areaId,
      hideDemo,
      setCompany,
      setAreaId,
      setHideDemo,
      areas,
      visibleAreas,
      areaById: (id) => (id ? areas.find((a) => a.id === id) : undefined),
      isLoading,
    }),
    [company, areaId, hideDemo, areas, visibleAreas, isLoading],
  );

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
}

export function useFilter(): FilterContextValue {
  const context = useContext(FilterContext);
  if (!context) throw new Error('useFilter må brukes inne i FilterProvider.');
  return context;
}

/** Query params shared by the endpoints that respect the global filter. */
export function useFilterParams(): {
  company?: Company;
  areaId?: string;
  excludeSeed?: boolean;
} {
  const { company, areaId, hideDemo } = useFilter();
  return {
    company: company === 'ALLE' ? undefined : company,
    areaId: areaId ?? undefined,
    // Only sent when on, so the default request stays as it was.
    excludeSeed: hideDemo || undefined,
  };
}
